const axios = require('@/utils/axios')
const {baseURL} = require('@/assets/js/config')
const $http = axios.create({
	baseURL
})

const state = {
	questions : [],
	current_question : null,
	pending_explanations : {
		data: [],
		pagination: {
			total: 0,
			current_page: 1,
			per_page: 20,
			total_pages: 0,
			from: 0,
			to: 0
		},
		loading: false,
		filters: {
			skill_id: null,
			category: null,
			grade_subject: null
		}
	}
}

const getters = {
	fetchQuestions : (state) => state.questions,
	fetchCurrentQuestion : (state) => state.current_question,
	fetchPendingExplanations : (state) => state.pending_explanations.data,
	fetchPendingExplanationsPagination : (state) => state.pending_explanations.pagination,
	isPendingExplanationsLoading : (state) => state.pending_explanations.loading,
	fetchPendingExplanationsFilters : (state) => state.pending_explanations.filters
}

const actions = {
	//Fetch list of all question banks
	loadQuestions({commit}, {token, filter, options={}}){
		const query_arr = []
		for(let key in filter){
			if(filter[key]){
				query_arr.push(`${key}=${encodeURIComponent(filter[key])}`)	
			}
		}
		const query_str = query_arr.join('&')
		return $http.get(`question?${query_str}`, {
			headers : {
				'x-auth-token' : token
			}
		}).then((response) => {
			let questions = response.data
			if(options?.firstQuestionId){
				questions = [questions.find(q => q._id === options.firstQuestionId), ...questions.filter(q => q._id !== options.firstQuestionId)]
			}
			commit('SET_QUESTIONS', questions)
		})
	},

	//Load pending explanations with pagination and filters
	async loadPendingExplanations({commit, state}, {token, page = null, per_page = null, filters = null}){
		commit('SET_PENDING_EXPLANATIONS_LOADING', true)
		
		// Use current state values if not provided
		const currentPage = page !== null ? page : state.pending_explanations.pagination.current_page
		const perPage = per_page !== null ? per_page : state.pending_explanations.pagination.per_page
		const currentFilters = filters !== null ? filters : state.pending_explanations.filters
		
		// Update filters in state
		if(filters !== null) {
			commit('SET_PENDING_EXPLANATIONS_FILTERS', filters)
		}
		
		const query_arr = []
		query_arr.push(`page=${currentPage}`)
		query_arr.push(`limit=${perPage}`)
		
		// Add filter parameters
		if(currentFilters.skill_id) {
			query_arr.push(`skill_id=${encodeURIComponent(currentFilters.skill_id)}`)
		}
		if(currentFilters.category) {
			query_arr.push(`category=${encodeURIComponent(currentFilters.category)}`)
		}
		if(currentFilters.grade_subject) {
			query_arr.push(`grade_subject=${encodeURIComponent(currentFilters.grade_subject)}`)
		}
		
		const query_str = query_arr.join('&')
		
		try {
			const response = await $http.get(`question/pending-explanations?${query_str}`, {
				headers : {
					'x-auth-token' : token
				}
			})
			
			const data = response.data
			
			// Handle different API response formats
			if(data.data && data.pagination) {
				// Standard paginated API response
				commit('SET_PENDING_EXPLANATIONS_DATA', {
					data: data.data,
					pagination: {
						total: data.pagination.total,
						current_page: data.pagination.current_page || currentPage,
						per_page: data.pagination.per_page || perPage,
						total_pages: data.pagination.total_pages || Math.ceil(data.pagination.total / (data.pagination.per_page || perPage)),
						from: data.pagination.from || ((currentPage - 1) * perPage + 1),
						to: data.pagination.to || Math.min(currentPage * perPage, data.pagination.total)
					}
				})
			} else if(data.results && data.count !== undefined) {
				// Alternative paginated format
				commit('SET_PENDING_EXPLANATIONS_DATA', {
					data: data.results,
					pagination: {
						total: data.count,
						current_page: currentPage,
						per_page: perPage,
						total_pages: Math.ceil(data.count / perPage),
						from: (currentPage - 1) * perPage + 1,
						to: Math.min(currentPage * perPage, data.count)
					}
				})
			} else {
				// Invalid response format
				throw new Error('Invalid API response format. Expected paginated data.')
			}
			
			commit('SET_PENDING_EXPLANATIONS_LOADING', false)
			
		} catch (error) {
			commit('SET_PENDING_EXPLANATIONS_LOADING', false)
			throw error
		}
	},

	// Change page and reload data
	changePendingExplanationsPage({dispatch, state}, {token, page}){
		return dispatch('loadPendingExplanations', {
			token,
			page,
			per_page: state.pending_explanations.pagination.per_page,
			filters: state.pending_explanations.filters
		})
	},

	// Change per page limit and reload data
	changePendingExplanationsPerPage({dispatch, state}, {token, per_page, page = 1}){
		return dispatch('loadPendingExplanations', {
			token,
			page,
			per_page,
			filters: state.pending_explanations.filters
		})
	},

	// Apply filters and reload data from page 1
	applyPendingExplanationsFilters({dispatch}, {token, filters}){
		return dispatch('loadPendingExplanations', {
			token,
			page: 1,
			per_page: 20,
			filters
		})
	},

	// Clear filters and reload data
	clearPendingExplanationsFilters({dispatch}, {token}){
		const filters = {
			skill_id: null,
			category: null,
			grade_subject: null
		}
		return dispatch('loadPendingExplanations', {
			token,
			page: 1,
			per_page: 20,
			filters
		})
	},

	// Refresh current page with current filters
	refreshPendingExplanations({dispatch, state}, {token}){
		return dispatch('loadPendingExplanations', {
			token,
			page: state.pending_explanations.pagination.current_page,
			per_page: state.pending_explanations.pagination.per_page,
			filters: state.pending_explanations.filters
		})
	},

	//Fetch a single question bank by ID
	loadQuestionDetails({commit}, {id, token}){
		return $http.get(`question/${id}`, {
			headers : {
				'x-auth-token' : token
			}
		}).then((response) => {
			console.log("SET_CURRENT_QUESTION", JSON.parse(JSON.stringify(response.data)))
			commit('SET_CURRENT_QUESTION', JSON.parse(JSON.stringify(response.data)))
		})
	},

	//Creating a new question in a question bank
	createQuestion({commit}, {question, token}){
		let question_formdata = new FormData()
		for(let key in question){
			if(key == 'files'){
				for(let i in question[key]){
					question_formdata.append(`${key}`, question[key][i])
				}
			}else if((key == 'layers') || (key == 'tables') || (key == 'stories') || (key == 'number_line')){
				question_formdata.append(key, JSON.stringify(question[key]))
			}else{
				question_formdata.append(key, question[key])
			}	
		}

		return $http.post(`question`, question_formdata, {
			headers : {
				'x-auth-token' : token,
				'Content-type' : 'undefined'
			}
		}).then((response) => {
			const question = response.data
			commit('ADD_QUESTION', question)
		})
	},

	uploadQuestionExplanationFile(obj, {file, token}){
		let question_formdata = new FormData()
		question_formdata.append('file', file)
		return $http.post(`question/explanation`, question_formdata, {
			headers : {
				'x-auth-token' : token,
				'Content-type' : 'undefined'
			}
		})
	},

	//Editing a question from question bank
	editQuestion({commit}, {id, question, token}){
		let question_formdata = new FormData()
		for(let key in question){
			if(key == 'files'){
				for(let i in question[key]){
					question_formdata.append(`${key}`, question[key][i])
				}
			}else if(key == 'editfiles'){
				question_formdata.append(key, JSON.stringify(question[key]))
			}else if((key == 'layers') || (key == 'tables') || (key == 'stories') || (key == 'number_line')){
				question_formdata.append(key, JSON.stringify(question[key]))
			}else{
				question_formdata.append(key, question[key])
			}	
		}
		
		return $http.put(`question/${id}`, question_formdata, {
			headers : {
				'x-auth-token' : token,
				'Content-type' : 'undefined'
			}
		}).then((response) => {
			const new_question = response.data
			commit('UPDATE_QUESTION', new_question)
		})
	},

	//Deleting a question from question bank
	deleteQuestion({commit}, {id, token}){
		return $http.delete(`question/${id}`, {
			headers : {
				'x-auth-token' : token
			}
		}).then(() => {
			commit('DELETE_QUESTION', id)
		})
	},

	// Copy questions from a skill to another skill
	copyQuestions(obj, {copy, token}){	
		return $http.post('question/copy', copy, {
			headers : {
				'x-auth-token' : token
			}
		})
	},

	appendQuestionToList({commit}, question_id){
		commit("APPEND_QUESTION_TO_LIST", question_id)
	},
}

const mutations = {
	SET_QUESTIONS : (state, all_qbs) => {
		state.questions = all_qbs
	},

	SET_PENDING_EXPLANATIONS_DATA : (state, {data, pagination}) => {
		state.pending_explanations.data = data
		state.pending_explanations.pagination = pagination
	},

	SET_PENDING_EXPLANATIONS_LOADING : (state, loading) => {
		state.pending_explanations.loading = loading
	},

	SET_CURRENT_QUESTION : (state, question) => {
		state.current_question = question
	},

	ADD_QUESTION : (state, qb) => {
		state.questions.push(qb)
	},

	UPDATE_QUESTION : (state, question) => {
		// Updating the question at that particular index (There can be multiple questions with same ids since it is being used inside quiz also)
		state.questions = state.questions.map(qb => qb._id === question._id? question : qb)
	},

	DELETE_QUESTION : (state, id) => {
		state.questions = state.questions.filter(qb => qb._id != id)
	},

	APPEND_QUESTION_TO_LIST : (state, question_id) => {
		state.questions = [...state.questions, state.questions.find(q => q._id === question_id)]
	},

	SET_PENDING_EXPLANATIONS_FILTERS : (state, filters) => {
		state.pending_explanations.filters = filters
	}
}

export default { 
	state, getters, actions, mutations
}